import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';

import useStores from 'hooks/use-stores';
import { SearchType } from 'main/components/SearchBar';
import { ElasticQueryBuilder } from 'main/modules/elastic';
import { stringifyQuery } from 'utils/stringify-query';

import { DIRECTIONS, FIELDS } from '../constants';

type Field = typeof FIELDS[number];
type Direction = typeof DIRECTIONS[number];
type Sorting = {
    field?: Field;
    direction?: Direction;
};

export default function useSortingState() {
    const history = useHistory();
    const { searchStore } = useStores();

    const sorting: Sorting = useMemo(() => {
        if (searchStore.type === SearchType.simple) {
            const [field, direction] = parseSortFromQuery(searchStore.query);
            return { field, direction };
        }

        if (searchStore.type === SearchType.elastic) {
            return { field: searchStore.requestState.sortField, direction: searchStore.requestState.sortDirection };
        }

        return {};
    }, [
        searchStore.query,
        searchStore.requestState.sortDirection,
        searchStore.requestState.sortField,
        searchStore.type
    ]);

    const isActive = useCallback((field: Field) => field === sorting.field && Boolean(sorting.direction), [sorting]);

    const updateUrl = useCallback(() => {
        const { query, type, requestState } = searchStore;
        const state = ElasticQueryBuilder.stateToString(requestState);

        switch (type) {
            case SearchType.simple:
                history.push(stringifyQuery('/search', { query }));
                break;
            case SearchType.elastic:
                history.push(stringifyQuery('/search', { state }));
                break;
            default:
                break;
        }
    }, [history, searchStore]);

    const refetch = useCallback(() => {
        searchStore.performFetch();
        setTimeout(() => updateUrl(), 0);
    }, [searchStore, updateUrl]);

    const toggle = useCallback(
        (field: Field) => {
            switch (true) {
                case searchStore.type === SearchType.simple:
                    searchStore.setQuery(
                        getQueryWithSort({
                            query: searchStore.query,
                            field,
                            direction: field === sorting.field ? nextDirection(sorting.direction) : 'desc'
                        })
                    );
                    refetch();
                    break;
                case searchStore.type === SearchType.elastic:
                    searchStore.elasticSearchDriver.setState({
                        sortField: field,
                        sortDirection: field === sorting.field ? nextDirection(sorting.direction) : 'desc'
                    });
                    refetch();
                    break;
                default:
                    break;
            }
        },
        [refetch, searchStore, sorting]
    );

    return {
        field: sorting.field,
        direction: sorting.direction,
        toggle,
        isActive,
        disabled: searchStore.type === SearchType.product
    };
}

function nextDirection(direction?: Direction): Direction {
    if (direction) {
        const next = DIRECTIONS.indexOf(direction) + 1;
        return DIRECTIONS[next % DIRECTIONS.length];
    } else {
        return 'asc';
    }
}

const SORT_REGEXP = /(?<direction>-?)order:(?<field>[^\s]+)/;

function parseSortFromQuery(query: string) {
    const match = query.match(SORT_REGEXP);

    if (match) {
        const field = match.groups?.field;
        if (field) {
            const direction = match.groups?.direction === '-' ? 'asc' : 'desc';
            return [field, direction];
        }
    }

    return [];
}

function getQueryWithSort(options: { query: string; field: Field; direction: Direction }) {
    const { query, field, direction } = options;
    const sign = direction === 'asc' ? '-' : '';
    const statement = `${sign}order:${field}`;
    return SORT_REGEXP.test(query) ? query.replace(SORT_REGEXP, statement) : `${query} ${statement}`;
}
